import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2, Save, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  getMenuItems,
  seedMenuItems,
  setMenuItems,
  type MenuItemCategory,
  type StoredMenuItem,
} from '@/lib/menuItems'
import type { MenuItem } from '@/components/maia/types'
import { MenuItemForm } from './MenuItemForm'

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

interface MenuItemsManagerProps {
  category: MenuItemCategory
  zonePrefix: string
}

export function MenuItemsManager({ category, zonePrefix }: MenuItemsManagerProps) {
  const { t } = useTranslation('admin')
  const { t: tMaia } = useTranslation('maia')
  const [savedItems, setSavedItems] = useState<StoredMenuItem[]>(() => getMenuItems(category))
  const [draft, setDraft] = useState<StoredMenuItem[]>(savedItems)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<StoredMenuItem | null>(null)

  const fallbackKey = category === 'starter' ? 'starters.items' : 'desserts.items'

  useEffect(() => {
    const fallback = tMaia(fallbackKey, { returnObjects: true }) as MenuItem[]
    seedMenuItems(category, fallback)
    const loaded = getMenuItems(category)
    setSavedItems(loaded)
    setDraft(loaded)
  }, [category, fallbackKey, tMaia])

  const isDirty = useMemo(() => {
    return JSON.stringify(draft) !== JSON.stringify(savedItems)
  }, [draft, savedItems])

  const sortedItems = useMemo(() => {
    return [...draft].sort((a, b) => b.createdAt - a.createdAt)
  }, [draft])

  const handleSaveChanges = () => {
    setMenuItems(category, draft)
    const updated = getMenuItems(category)
    setSavedItems(updated)
    setDraft(updated)
  }

  const handleDiscardChanges = () => {
    setDraft(savedItems)
  }

  const handleAddToDraft = (
    data: Omit<StoredMenuItem, 'id' | 'createdAt' | 'updatedAt'> & { id?: string }
  ) => {
    if (data.id) {
      setDraft((prev) =>
        prev.map((item) =>
          item.id === data.id
            ? { ...item, ...data, updatedAt: Date.now() }
            : item
        )
      )
    } else {
      const created: StoredMenuItem = {
        ...data,
        id: generateId(),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      setDraft((prev) => [created, ...prev])
    }
  }

  const handleAdd = () => {
    setSelectedItem(null)
    setIsModalOpen(true)
  }

  const handleEdit = (item: StoredMenuItem) => {
    setSelectedItem(item)
    setIsModalOpen(true)
  }

  const handleDelete = (id: string) => {
    setDraft((prev) => prev.filter((item) => item.id !== id))
  }

  const handleClose = () => {
    setIsModalOpen(false)
    setSelectedItem(null)
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-cord bg-white">
        <header className="flex flex-wrap items-center justify-between gap-3 bg-maia-soft px-5 py-4">
          <h3 className="font-display text-lg font-bold text-ink">
            {t(`${zonePrefix}.title`)}
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSaveChanges}
              disabled={!isDirty}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
                isDirty
                  ? 'bg-maia text-white shadow-sm hover:bg-maia-dark'
                  : 'cursor-not-allowed border border-dashed border-muted/40 bg-transparent text-muted/60'
              )}
            >
              <Save className="h-3.5 w-3.5" aria-hidden="true" />
              {t(`${zonePrefix}.saveChanges`)}
            </button>
            <button
              type="button"
              onClick={handleDiscardChanges}
              disabled={!isDirty}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors',
                isDirty
                  ? 'bg-white text-ink shadow-sm hover:bg-cream'
                  : 'cursor-not-allowed border border-dashed border-muted/40 bg-transparent text-muted/60'
              )}
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              {t(`${zonePrefix}.discardChanges`)}
            </button>
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-1.5 rounded-full bg-maia px-3 py-1.5 text-xs font-bold text-white shadow-sm transition-colors hover:bg-maia-dark"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              {t(`${zonePrefix}.addDish`)}
            </button>
          </div>
        </header>

        {sortedItems.length === 0 ? (
          <div className="px-5 py-8 text-center">
            <p className="text-sm text-muted">{t(`${zonePrefix}.empty`)}</p>
          </div>
        ) : (
          <>
            <div className="sm:hidden divide-y divide-cord">
              {sortedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  {item.image ? (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-14 w-14 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-lg bg-cord text-[10px] font-medium text-muted">
                      {t(`${zonePrefix}.noPhoto`)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{item.title}</p>
                    <p className="text-sm text-muted">{item.price || '-'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleEdit(item)}
                      aria-label={t(`${zonePrefix}.editDish`)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink/10 text-ink transition-colors hover:bg-maia hover:text-white"
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      aria-label={t(`${zonePrefix}.delete`)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ink/10 text-ink transition-colors hover:bg-pizza hover:text-white"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-cord text-xs uppercase tracking-wider text-muted">
                  <th className="py-3 pl-5 pr-4 font-semibold">
                    {t(`${zonePrefix}.table.photo`)}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t(`${zonePrefix}.table.title`)}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t(`${zonePrefix}.table.description`)}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t(`${zonePrefix}.table.allergens`)}
                  </th>
                  <th className="py-3 pr-4 font-semibold">
                    {t(`${zonePrefix}.table.price`)}
                  </th>
                  <th className="py-3 pr-5 font-semibold">
                    {t(`${zonePrefix}.table.actions`)}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cord">
                {sortedItems.map((item) => (
                  <tr
                    key={item.id}
                    className="transition-colors hover:bg-cream/60"
                  >
                    <td className="py-3 pl-5 pr-4">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          className="h-12 w-12 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-cord text-[10px] font-medium text-muted">
                          {t(`${zonePrefix}.noPhoto`)}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 font-medium text-ink">{item.title}</td>
                    <td className="max-w-xs py-3 pr-4">
                      <p className="truncate text-ink/80">
                        {item.description || '-'}
                      </p>
                    </td>
                    <td className="py-3 pr-4 text-ink/80">
                      {item.allergens || '-'}
                    </td>
                    <td className="py-3 pr-4 font-medium text-ink">
                      {item.price || '-'}
                    </td>
                    <td className="py-3 pr-5">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          aria-label={t(`${zonePrefix}.editDish`)}
                          className="inline-flex items-center rounded-full bg-ink/10 p-1.5 text-ink transition-colors hover:bg-maia hover:text-white"
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          aria-label={t(`${zonePrefix}.delete`)}
                          className="inline-flex items-center rounded-full bg-ink/10 p-1.5 text-ink transition-colors hover:bg-pizza hover:text-white"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <MenuItemForm
        isOpen={isModalOpen}
        category={category}
        zonePrefix={zonePrefix}
        initialItem={selectedItem}
        onClose={handleClose}
        onSave={handleAddToDraft}
      />
    </>
  )
}
